import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, Phone } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { API, PHONE_DISPLAY, PHONE_TEL } from "@/lib/content";
import { Reveal } from "./Reveal";
import { BookingForm } from "./BookingForm";

const today = new Date(new Date().setHours(0, 0, 0, 0));

export const Booking = () => {
  const [weekdays, setWeekdays] = useState([]);
  const [day, setDay] = useState();
  const [times, setTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [time, setTime] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    axios.get(`${API}/bookings/schedule`).then(({ data }) => setWeekdays(data.weekdays)).catch(() => {});
  }, []);

  const loadTimes = async (d) => {
    setLoadingTimes(true);
    try {
      const { data } = await axios.get(`${API}/bookings/availability`, { params: { date: format(d, "yyyy-MM-dd") } });
      setTimes(data.times);
    } catch {
      toast.error("We couldn't load times for that day.");
    } finally {
      setLoadingTimes(false);
    }
  };

  const pickDay = (d) => {
    setDay(d);
    setTime(null);
    setTimes([]);
    if (d) loadTimes(d);
  };

  // JS getDay: 0=Sun; backend weekday: 0=Mon
  const isClosed = (d) => d < today || !weekdays.includes((d.getDay() + 6) % 7);

  const onBooked = (b) => {
    setConfirmed(b);
    setTime(null);
    loadTimes(day);
  };

  return (
    <section id="book" className="bg-sage text-pine py-24 md:py-32 scroll-mt-20" data-testid="booking-section">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12">
        <Reveal className="min-w-0">
          <h2 className="dp">Book a <em>time slot</em></h2>
          <p className="mt-4 text-moss">A 50-minute coaching conversation by phone or video.</p>
          <p className="mt-10 lb">Prefer to talk now?</p>
          <p className="dp text-4xl tn mt-2" data-testid="booking-phone-number">{PHONE_DISPLAY}</p>
          <a href={PHONE_TEL} className="mt-4 inline-flex items-center gap-2 rounded-full border border-pine px-6 py-3 transition-colors hover:bg-pine hover:text-sage" data-testid="booking-call-button">
            <Phone size={16} /> Call us
          </a>
          <p className="mt-4 max-w-sm text-sm text-moss">If we can't pick up, our answering service will take your call and pass it on.</p>
        </Reveal>

        <Reveal delay={120} className="bg-paper p-6 sm:p-8 rounded-2xl min-w-0" data-testid="booking-panel">
          {confirmed ? (
            <div data-testid="booking-confirmation">
              <p className="lb text-brass-ink">You're booked</p>
              <p className="dp text-3xl mt-3">{format(new Date(`${confirmed.date}T12:00:00`), "EEEE, MMMM d")} · {confirmed.time}</p>
              <p className="mt-3 text-moss">Thank you, {confirmed.name}. We'll reach you by {confirmed.format} at the time you chose.</p>
              <button type="button" onClick={() => setConfirmed(null)} className="mt-6 underline underline-offset-4 decoration-brass" data-testid="booking-book-another-button">Book another time</button>
            </div>
          ) : (
            <>
              <p className="lb text-brass-ink">1 · Choose a day</p>
              <Calendar
                mode="single"
                selected={day}
                onSelect={pickDay}
                disabled={isClosed}
                className="mt-2 -mx-3"
                classNames={{ day_today: "underline decoration-brass underline-offset-4" }}
                data-testid="booking-calendar"
              />
              <p className="lb text-brass-ink mt-4">2 · Choose a time <span className="normal-case tracking-normal">(Central Time)</span></p>
              <div className="mt-3 grid grid-cols-2 gap-3 tn" data-testid="booking-times">
                {!day && <p className="col-span-2 text-sm text-moss">Pick a highlighted day to see open times.</p>}
                {loadingTimes && <Loader2 className="animate-spin text-brass" />}
                {day && !loadingTimes && times.map((t) => (
                  <button
                    key={t.time}
                    type="button"
                    disabled={!t.available}
                    onClick={() => setTime(t.time)}
                    className={`rounded-full border py-3 transition-colors disabled:opacity-40 disabled:line-through ${
                      time === t.time ? "border-pine bg-pine text-sage" : "border-pine/30 hover:border-brass"
                    }`}
                    data-testid={`booking-time-${t.time.replace(/[^0-9APM]/g, "")}`}
                  >
                    {format(day, "EEE")} · {t.time}
                  </button>
                ))}
              </div>
              {day && time && <BookingForm day={day} time={time} onBooked={onBooked} />}
            </>
          )}
        </Reveal>
      </div>
    </section>
  );
};
